import { create } from 'zustand';

export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  targetRole?: 'admin' | 'user'; // Which session type this notification belongs to
  link?: string | null;
  /**
   * Stable identity for a notification the client generates from live state,
   * such as "Profile Incomplete".
   *
   * These are recreated by a poll every 30 seconds, so without something
   * stable to recognise them by, dismissing one only removed that instance -
   * the next poll saw none present and made a fresh one. A key lets a
   * dismissal be remembered against the *thing being reported* rather than
   * against a row that is about to be replaced.
   */
  key?: string;
}

interface NotificationStore {
  notifications: Notification[];
  unreadCount: number;
  currentSessionType: 'admin' | 'user' | null;
  /** Keys the user has dismissed; generated notifications honour these. */
  dismissedKeys: string[];
  setSessionType: (type: 'admin' | 'user' | null) => void;
  /** Scope dismissals to one account, and load what they dismissed before. */
  loadDismissed: (scope: string | number | null) => void;
  dismissKey: (key: string) => void;
  /** Let a key fire again once the condition behind it has genuinely cleared. */
  undismissKey: (key: string) => void;
  /** The condition cleared on its own: drop the notice and its dismissal. */
  resolveKey: (key: string) => void;
  isDismissed: (key: string) => boolean;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  upsertNotifications: (notifications: Notification[]) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  getFilteredNotifications: () => Notification[];
  getFilteredUnreadCount: () => number;
}

/**
 * Dismissals live in localStorage, scoped per account.
 *
 * The store itself is in-memory, so a page reload would otherwise bring a
 * dismissed notice straight back. Scoping by user id keeps one person's
 * dismissals from following another into a shared browser.
 */
const DISMISS_PREFIX = 'baraza:dismissed-notifications:';
let dismissStorageKey: string | null = null;

const readDismissed = (storageKey: string): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === 'string') : [];
  } catch {
    return [];
  }
};

const writeDismissed = (keys: string[]) => {
  if (typeof window === 'undefined' || !dismissStorageKey) return;
  try {
    window.localStorage.setItem(dismissStorageKey, JSON.stringify(keys));
  } catch {
    // A full or blocked storage must not break the bell.
  }
};

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  currentSessionType: null,
  dismissedKeys: [],

  setSessionType: (type) => set({ currentSessionType: type }),

  loadDismissed: (scope) => {
    dismissStorageKey = `${DISMISS_PREFIX}${scope ?? 'anon'}`;
    set({ dismissedKeys: readDismissed(dismissStorageKey) });
  },

  dismissKey: (key) => set((state) => {
    if (!key || state.dismissedKeys.includes(key)) return state;
    const next = [...state.dismissedKeys, key];
    writeDismissed(next);
    return { dismissedKeys: next };
  }),

  undismissKey: (key) => set((state) => {
    if (!key || !state.dismissedKeys.includes(key)) return state;
    const next = state.dismissedKeys.filter((k) => k !== key);
    writeDismissed(next);
    return { dismissedKeys: next };
  }),

  isDismissed: (key) => !!key && get().dismissedKeys.includes(key),

  resolveKey: (key) => set((state) => {
    // The condition behind a generated notice has cleared - the profile got
    // completed, the draft got submitted. That is not the user dismissing it,
    // so drop the dismissal too: if they undo the fix later, the notice has
    // earned the right to come back.
    if (!key) return state;
    const remaining = state.notifications.filter((n) => n.key !== key);
    const dismissedKeys = state.dismissedKeys.filter((k) => k !== key);
    if (dismissedKeys.length !== state.dismissedKeys.length) writeDismissed(dismissedKeys);
    return {
      notifications: remaining,
      unreadCount: remaining.filter((n) => !n.read).length,
      dismissedKeys,
    };
  }),

  addNotification: (notification) => set((state) => {
    // A keyed notification the user has dismissed must not come back. This is
    // the guard that stops the 30-second poll from resurrecting it.
    if (notification.key && state.dismissedKeys.includes(notification.key)) {
      return state;
    }
    // Nor should a second copy stack up while one is already showing.
    if (notification.key && state.notifications.some((n) => n.key === notification.key)) {
      return state;
    }
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      read: false,
      // Default to 'user' if not specified (for backward compatibility)
      targetRole: notification.targetRole || 'user',
    };
    return {
      notifications: [newNotification, ...state.notifications],
      unreadCount: state.unreadCount + 1,
    };
  }),

  upsertNotifications: (incoming) => set((state) => {
    const byId = new Map<string, Notification>();
    for (const n of state.notifications) byId.set(n.id, n);
    for (const n of incoming) {
      const prev = byId.get(n.id);
      byId.set(n.id, prev ? { ...prev, ...n } : n);
    }
    const merged = Array.from(byId.values()).sort((a, b) => {
      const at = new Date(a.timestamp).getTime();
      const bt = new Date(b.timestamp).getTime();
      return bt - at;
    });
    const unread = merged.filter((n) => !n.read).length;
    return { notifications: merged, unreadCount: unread };
  }),
  
  markAsRead: (id) => set((state) => {
    const notification = state.notifications.find((n) => n.id === id);
    const wasUnread = notification && !notification.read;
    return {
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      ),
      unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
    };
  }),
  
  markAllAsRead: () => set((state) => {
    const filtered = state.currentSessionType
      ? state.notifications.filter(n => n.targetRole === state.currentSessionType)
      : state.notifications;
    const unreadInFiltered = filtered.filter(n => !n.read).length;

    // "Mark all read" has to count as dismissal for generated notices too.
    // The generator looks for an *unread* copy, so marking one read used to
    // make it invisible to that check and a fresh unread copy appeared.
    const newlyDismissed = filtered
      .map((n) => n.key)
      .filter((k): k is string => !!k && !state.dismissedKeys.includes(k));
    const dismissedKeys = newlyDismissed.length
      ? [...state.dismissedKeys, ...newlyDismissed]
      : state.dismissedKeys;
    if (newlyDismissed.length) writeDismissed(dismissedKeys);

    return {
      notifications: state.notifications.map((n) => {
        // Only mark as read if it matches current session type
        if (state.currentSessionType && n.targetRole !== state.currentSessionType) {
          return n;
        }
        return { ...n, read: true };
      }),
      unreadCount: Math.max(0, state.unreadCount - unreadInFiltered),
      dismissedKeys,
    };
  }),

  removeNotification: (id) => set((state) => {
    const notification = state.notifications.find((n) => n.id === id);

    // Clearing a generated notice is a decision about the underlying thing,
    // not about this one row, so remember it.
    let dismissedKeys = state.dismissedKeys;
    if (notification?.key && !dismissedKeys.includes(notification.key)) {
      dismissedKeys = [...dismissedKeys, notification.key];
      writeDismissed(dismissedKeys);
    }

    return {
      notifications: state.notifications.filter((n) => n.id !== id),
      unreadCount: notification && !notification.read ? state.unreadCount - 1 : state.unreadCount,
      dismissedKeys,
    };
  }),

  clearAll: () => set((state) => {
    const dismissedKeys = Array.from(new Set([
      ...state.dismissedKeys,
      ...state.notifications.map((n) => n.key).filter((k): k is string => !!k),
    ]));
    writeDismissed(dismissedKeys);
    return { notifications: [], unreadCount: 0, dismissedKeys };
  }),
  
  getFilteredNotifications: () => {
    const state = get();
    if (!state.currentSessionType) {
      return state.notifications;
    }
    return state.notifications.filter(n => n.targetRole === state.currentSessionType);
  },
  
  getFilteredUnreadCount: () => {
    const state = get();
    if (!state.currentSessionType) {
      return state.unreadCount;
    }
    return state.notifications.filter(
      n => n.targetRole === state.currentSessionType && !n.read
    ).length;
  },
}));